import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DvFormSectionsComponent,
  DvFormUtil,
  DvFormWrapperComponent,
  FormOf,
} from '@devinco/ui/dds';
import { DvSavePanelModule } from '@devinco/ui/legacy';
import { TranslateModule } from '@ngx-translate/core';
import { VippsMerchantDto } from './interfaces';
import { VippsIntegrationFormService } from './vipps-integration-form.service';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { map, Observable, take, tap } from 'rxjs';
import { FormTranslationDirective } from '@shared/dv-form/directives/dv-form-translate.directive';
import { RestApiService } from '@core/services/rest-api.service';
import { Entity } from '@shared/constants/entity';

@Component({
  selector: 'app-vipps-integration',
  imports: [
    DvFormWrapperComponent,
    DvSavePanelModule,
    TranslateModule,
    DvFormSectionsComponent,
    FormTranslationDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './vipps-integration.component.html',
})
export class VippsIntegrationComponent implements PendingChanges {
  private formService = inject(VippsIntegrationFormService);
  private api = inject(RestApiService);

  formGroup: FormGroup<FormOf<VippsMerchantDto>>;
  elements = this.formService.getElements();

  constructor() {
    this.formGroup = DvFormUtil.buildFormGroup(this.elements);
    this.setFormData();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  save(): void {
    this.performSave().subscribe();
  }

  private performSave(): Observable<boolean> {
    const { VippsMerchantID } = this.formGroup.controls;
    const entityId = VippsMerchantID?.value || 0;
    return this.api
      .upsert(
        Entity.VippsMerchant,
        entityId,
        DvFormUtil.getDirtyValues(this.formGroup)
      )
      .pipe(
        tap(id => {
          if (!entityId) {
            VippsMerchantID?.setValue(id);
          }
          this.formGroup.markAsPristine();
        }),
        map(() => true)
      );
  }

  private setFormData(): void {
    this.getMerchantData()
      .pipe(take(1))
      .subscribe(resp => {
        if (resp) {
          this.formGroup.patchValue(resp);
        }
      });
  }

  private getMerchantData(): Observable<VippsMerchantDto | undefined> {
    return this.api
      .getData<VippsMerchantDto>({
        entity: Entity.VippsMerchant,
        fields: this.elements
          .map(el => el.formControlParams!.key)
          .filter(key => !!key),
      })
      .pipe(map(resp => resp.data?.[0] || undefined));
  }
}
